import { inject, Injectable } from '@angular/core';
import { Observable, BehaviorSubject, map, tap, catchError, EMPTY } from 'rxjs';
import moment from 'jalali-moment';
import { FundConstants } from '../../../constants/fund.constants';
import { PortfolioConstants } from '../../../constants/portfolio.constants';
import {
	OfflineIpoOrder,
	OfflineIpoPriceTextStatus,
	OfflineIpoCreateRequest,
	OfflineOrderState,
	OfflineIpoUpdateRequest
} from '../../offline-ipo/models/offline-ipo.model';
import { Side, EasyState } from '../../order/oms/models/orderModels.model';
import { SymbolService } from '../../shared/services/symbol.service';
import { getDistanceToDate, checkDateByRangeDates } from '../../../tools/dates/ipo-date.tool';
import { calculateTotalExpense } from '../../../tools/order-calc.tool';
import { DateHelper } from '../../../tools/primptives-tool/date-tool';
import { Ipo, IpoAndNoneEtfSubscriptionResponse, NoneEtf } from '../models/market-watch-list.model';
import { BaseService } from '../../shared/services';
import { TimeService } from '../../shared/services/time.service';
import { OrderModelType, apiUrls } from '../../../constants';
import { OfflineOrder } from '../../order';

@Injectable({
	providedIn: 'root'
})
export class IpoService extends BaseService {
	private readonly timeService: TimeService = inject(TimeService);
	private readonly symbolService: SymbolService = inject(SymbolService);
	private iposSub = new BehaviorSubject<Ipo[]>([]);
	public iposObs$ = this.iposSub.asObservable();

	private noneEtfsSub = new BehaviorSubject<NoneEtf[]>(undefined);
	public noneEtfsObs$ = this.noneEtfsSub.asObservable();

	private offlineIposSub = new BehaviorSubject<OfflineIpoOrder[]>([]);
	public offlineIposObs$ = this.offlineIposSub.asObservable();

	private lastIpoApiCallDate = 0;
	private serverDate: Date;

	ipoByIsin: Record<string, Partial<Ipo>>;
	noneEtfByFundCode: Record<string, Partial<NoneEtf>>;

	constructor() {
		super();
		this.subscribeToIpos();
		this.subscribeToNoneEtfs();
		this.timeService.serverDateObs$.subscribe((date: Date) => (this.serverDate = date));
	}

	private get ipoUrl(): string {
		return `${this.mtsPath + apiUrls.easy}MarketWatch/ipos`;
	}

	private get offlineIpoUrl(): string {
		return `${this.mtsPath + apiUrls.easy}offline-ipo`;
	}

	private subscribeToIpos(): void {
		this.iposObs$.subscribe(ipos => {
			if (ipos) this.ipoByIsin = Object.fromEntries(ipos?.map(ipo => [ipo.symbolIsin, ipo]));
		});
	}

	private subscribeToNoneEtfs(): void {
		this.noneEtfsObs$.subscribe(noneEtfs => {
			if (noneEtfs)
				this.noneEtfByFundCode = Object.fromEntries(noneEtfs?.map(noneEtf => [noneEtf.fundCode, noneEtf]));
		});
	}

	getIpos(): Ipo[] {
		return this.iposSub.value;
	}

	getIpoByIsin(symbolIsin: string): Ipo {
		return this.ipoByIsin[symbolIsin] as Ipo;
	}

	getNoneEtfs(): NoneEtf[] {
		return this.noneEtfsSub.value;
	}

	getNoneEtfByFundCode(fundCode: string): NoneEtf {
		return this.noneEtfByFundCode[fundCode] as NoneEtf;
	}
	getIpoAndNoneEtf(hardLoad = false): Observable<IpoAndNoneEtfSubscriptionResponse> {
		const currentDate = new Date().getTime() / 1000;
		if (
			hardLoad ||
			currentDate - this.lastIpoApiCallDate > PortfolioConstants.TimePeriodForRefreshingMarketWatchSeconds
		) {
			this.lastIpoApiCallDate = currentDate;
			return this.httpService.get<IpoAndNoneEtfSubscriptionResponse>(this.ipoUrl).pipe(
				tap(res => {
					this.fillIpo(res);
					this.fillNoneEtf(res);
				}),
				catchError(() => {
					this.fillNoneEtf({ ipos: [], noneEtfSubscriptions: [] });
					return EMPTY;
				})
			);
		} else {
			return EMPTY;
		}
	}

	private async fillIpo(res: IpoAndNoneEtfSubscriptionResponse) {
		if (!res?.ipos) return this.iposSub.next([]);

		const ipos = [];
		for (const ipo of res.ipos) {
			ipo.date = ipo.date.trim();
			ipo.toDate = ipo.toDate.trim();
			const symbol = await this.symbolService.getSymbol(ipo.symbolIsin);
			const date =
				ipo.date.length > 1
					? `${DateHelper.getJDayOfWeek(moment.from(ipo.date, 'fa', 'YYYY/M/D').day())} ${ipo.date}`
					: '';
			const endDate =
				ipo.toDate.length > 1
					? `${DateHelper.getJDayOfWeek(moment.from(ipo.toDate, 'fa', 'YYYY/M/D').day())} ${ipo.toDate}`
					: '';
			const startTime = ipo.time && ipo.time !== '-' ? ipo.time.substring(0, ipo.time.lastIndexOf('-')) : '';
			const endTime =
				ipo.time && ipo.time !== '-' ? ipo.time.substring(ipo.time.lastIndexOf('-') + 1) : 'طبق پیام ناظر';
			const distanceToDate = getDistanceToDate(this.serverDate, ipo.date, ipo.toDate, startTime, endTime, 'عرضه');
			const dateStatus = checkDateByRangeDates(this.serverDate, ipo.date, ipo.toDate, ipo.time);
			const maxMoneyNeeded = calculateTotalExpense(
				Side.Buy,
				ipo.maxPrice,
				ipo.share,
				+symbol?.buyCommission || 0
			);

			const priceTextStatus =
				ipo.share && ipo.minPrice
					? OfflineIpoPriceTextStatus.MaxMoneyNeeded
					: ipo.description
						? OfflineIpoPriceTextStatus.WithDescription
						: OfflineIpoPriceTextStatus.Unknown;

			ipos.push({
				...ipo,
				date,
				endDate,
				pureDate: ipo.date,
				pureDateEnd: ipo.toDate,
				symbolFullName: symbol?.title || '',
				commission: +symbol?.buyCommission || 0,
				startTime,
				endTime,
				distanceToDate,
				dateStatus,
				maxMoneyNeeded,
				priceTextStatus: priceTextStatus
			});
		}
		this.iposSub.next(ipos);
	}

	private fillNoneEtf(res: IpoAndNoneEtfSubscriptionResponse) {
		if (!res?.noneEtfSubscriptions) return this.noneEtfsSub.next([]);

		const noneEtfs = [];
		for (const noneEtf of res.noneEtfSubscriptions) {
			const date = noneEtf.date
				? `${DateHelper.getJDayOfWeek(moment.from(noneEtf.date, 'fa', 'YYYY/M/D').day())} ${noneEtf.date}`
				: '';
			const endDate = noneEtf.toDate
				? `${DateHelper.getJDayOfWeek(moment.from(noneEtf.toDate, 'fa', 'YYYY/M/D').day())} ${noneEtf.toDate}`
				: '';
			const startTime =
				noneEtf.time && noneEtf.time !== '-' ? noneEtf.time.substring(0, noneEtf.time.lastIndexOf('-')) : '';
			const endTime =
				noneEtf.time && noneEtf.time !== '-'
					? noneEtf.time.substring(noneEtf.time.lastIndexOf('-') + 1)
					: 'طبق پیام ناظر';
			const distanceToDate = getDistanceToDate(
				this.serverDate,
				noneEtf.date,
				noneEtf.toDate,
				startTime,
				endTime,
				'پذیره نویسی'
			);
			const dateStatus = checkDateByRangeDates(this.serverDate, noneEtf.date, noneEtf.toDate, noneEtf.time);

			noneEtfs.push({
				...noneEtf,
				date,
				endDate,
				pureDate: noneEtf.date,
				pureDateEnd: noneEtf.toDate,
				description: noneEtf.description,
				logo: FundConstants.FundIcon[noneEtf.fundCode],
				distanceToDate,
				startTime,
				endTime,
				dateStatus
			});
		}
		this.noneEtfsSub.next(noneEtfs);
	}

	getOfflineIpo(symbolIsins: string[]): Observable<OfflineIpoOrder[]> {
		return this.httpService.get<OfflineIpoOrder[]>(`${this.offlineIpoUrl}/${symbolIsins.join(',')}`).pipe(
			tap((newItems: OfflineIpoOrder[]) => {
				newItems.forEach(o => (o.offlineSendStrategy = o.sendStrategy));
				this.offlineIposSub.next(newItems);
			})
		);
	}

	createOfflineIpo(ipo: OfflineIpoCreateRequest): Observable<OfflineOrder> {
		return this.httpService
			.post<any>(this.offlineIpoUrl, {
				body: { order: { ...ipo } }
			})
			.pipe(
				map(newItem => {
					const newOrder = new OfflineOrder({
						symbolIsin: ipo.symbolIsin,
						createDateTime: new Date().toString(),
						side: Side.Buy,
						id: newItem.id,
						easyState: EasyState.Done,
						errorDescription: '',
						orderModelType: OrderModelType.offline,
						price: ipo.price,
						quantity: ipo.quantity,
						state: OfflineOrderState.Offline,
						sendStrategy: ipo.offlineSendStrategy
					} as OfflineOrder);

					const symbol = this.symbolService.symbolByIsin?.[newOrder.symbolIsin];
					newOrder.symbolName = symbol?.symbolName || ipo.symbolName;
					return newOrder;
				})
			);
	}

	updateOfflineIpo(ipo: OfflineIpoUpdateRequest): Observable<OfflineOrder> {
		return this.httpService
			.put<any>(this.offlineIpoUrl, {
				body: { order: { ...ipo } }
			})
			.pipe(
				map(() => {
					const newOrder = new OfflineOrder({
						symbolIsin: ipo.symbolIsin,
						createDateTime: ipo.createDateTime,
						side: Side.Buy,
						id: ipo.orderId,
						easyState: EasyState.Done,
						errorDescription: '',
						orderModelType: OrderModelType.offline,
						price: ipo.price,
						quantity: ipo.quantity,
						state: OfflineOrderState.Offline
					} as OfflineOrder);
					return newOrder;
				})
			);
	}

	deleteOfflineIpo(ipoId: string): Observable<void> {
		return this.httpService.delete<void>(`${this.offlineIpoUrl}/${ipoId}`).pipe(
			tap(() => {
				const prevItems = this.offlineIposSub.value;
				const items = prevItems.filter(i => i.id !== ipoId);
				this.offlineIposSub.next(items);
				this.iposSub.next(this.iposSub.value);
			})
		);
	}
	getSymbol(isin) {
		return this.symbolService.getSymbol(isin);
	}
	getOfflineOrdersOperator = (omsOrders, ipos: Ipo[]) => {
		const symbolIsins = ipos.map(a => a.symbolIsin);
		return this.getOfflineIpo(symbolIsins).pipe(
			map((offlineOrders: OfflineIpoOrder[]) => {
				const orderIsins = omsOrders.map(o => o.symbolIsin);
				return offlineOrders
					.filter(oi => !orderIsins.includes(oi.symbolIsin))
					.filter(oi => symbolIsins.includes(oi.symbolIsin))
					.map(
						item =>
							new OfflineOrder({
								symbolIsin: item.symbolIsin,
								createDateTime: item.createDateTime,
								side: Side.Buy,
								id: item.id,
								easyState: EasyState.Done,
								errorDescription: '',
								orderModelType: OrderModelType.offline,
								price: item.price,
								quantity: item.quantity,
								state: item.state,
								errorType: item.errorType,
								symbolName: ipos.find(x => x.symbolIsin === item.symbolIsin).symbolName
							} as OfflineOrder)
					);
			})
		);
	};
}
